// ─── TanStack Query Client ───

import { QueryClient, MutationCache, type Mutation, type Query } from '@tanstack/react-query';
import { toast } from 'sonner';
import { VERSION_CONFLICT_PROBLEM_TYPE } from '@a8-planner/shared/types/api';
import { ApiError } from './api-client';
import { getConnection } from './signalr';
import i18n from './i18n';
import { translateProblemDetail, type ValidationTFunction } from './i18n/validation';

/**
 * Global mutation error feedback. Before this existed, failed mutations
 * rolled back their optimistic updates silently — the UI just "snapped back"
 * with no explanation (the 409 optimistic-locking case was the worst:
 * your edit vanished because someone else changed the row first).
 *
 * Runs for EVERY failed mutation in addition to (not instead of) the
 * mutation's own onError rollback. Mutations that render their own error UI
 * (e.g. forms with inline errors) can opt out with
 * `meta: { suppressErrorToast: true }`.
 *
 * Exported for unit testing.
 */
export function handleMutationError(
  error: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
): void {
  if (mutation.meta?.suppressErrorToast === true) return;

  const t = i18n.getFixedT(null, 'common');
  const tValidation = i18n.getFixedT(null, 'validation') as ValidationTFunction;

  // An optimistic-locking failure is the ONE 409 we can diagnose for the user, and only
  // because the API tags it with a dedicated RFC 7807 type. Every other 409 (unique-name
  // collision, a status that still has tasks, an immutable published questionnaire) used
  // to be swept into this branch and mis-reported as "someone else changed it — please
  // retry", advice that could never succeed. See VERSION_CONFLICT_PROBLEM_TYPE.
  if (error instanceof ApiError && error.type === VERSION_CONFLICT_PROBLEM_TYPE) {
    toast.error(t('mutationError.versionConflict.title'), {
      description: t('mutationError.versionConflict.description'),
      action: {
        label: t('mutationError.versionConflict.action'),
        // The old copy claimed "the view has been refreshed" while nothing refreshed.
        // Now the claim is an offer, and this makes it true.
        onClick: () => void queryClient.invalidateQueries(),
      },
    });
    return;
  }

  if (error instanceof ApiError) {
    // Shared Zod schemas emit stable i18n KEYS as their messages, and the API passes them
    // through untouched, so a 400's `detail` reads `scopeId: dashboard.scopeIdRequired`
    // until it is mapped through the validation catalog.
    const description = translateProblemDetail(tValidation, error.detail) ?? error.detail;
    toast.error(t('mutationError.generic.title'), {
      description: description || t('mutationError.generic.description'),
    });
    return;
  }

  // Non-ApiError (network failure, a thrown TypeError). The raw message is untranslated
  // developer English, so it is DEV-only — the same gating ErrorFallback.tsx uses.
  toast.error(t('mutationError.generic.title'), {
    description:
      import.meta.env.DEV && error instanceof Error
        ? error.message
        : t('mutationError.generic.description'),
  });
}

/**
 * Query roots that SignalR would otherwise keep fresh by server push (#272). Kept in step
 * with `EVENT_INVALIDATION_MAP` in `hooks/useSignalR.ts` — if the hub stops pushing a key,
 * polling has to cover it.
 */
const REALTIME_BACKED_QUERY_ROOTS = new Set(['tasks', 'comments']);

/** Matches the notifications fallback cadence (`hooks/useNotifications.ts`). */
const REALTIME_FALLBACK_POLL_MS = 30_000;

/**
 * The polling half of ADR-0009's documented "gracefully degrades to REST polling".
 *
 * That fallback did not actually exist for tasks or comments: the only `refetchInterval`
 * in the app was the notification count's. Combined with `staleTime: 60s` and
 * `refetchOnWindowFocus: false`, a collaborative planner could serve arbitrarily stale
 * data while looking authoritative — and, since the socket failure was silent (#272),
 * without ever telling the user.
 *
 * Implemented as a default rather than per-call-site so a new task/comment query cannot
 * forget it. Returns `false` while the socket is healthy, so a working connection costs
 * nothing. `refetchIntervalInBackground` stays at its default of false, so a hidden tab
 * does not poll.
 */
export function realtimeFallbackRefetchInterval(query: Query): number | false {
  const root = query.queryKey[0];
  if (typeof root !== 'string' || !REALTIME_BACKED_QUERY_ROOTS.has(root)) return false;
  return getConnection()?.state === 'Connected' ? false : REALTIME_FALLBACK_POLL_MS;
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      handleMutationError(error, mutation);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      gcTime: 1000 * 60 * 5, // 5 minutes (garbage collection time)
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: realtimeFallbackRefetchInterval,
    },
    mutations: {
      retry: 0,
    },
  },
});
